"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.GoogleMap = void 0;
var _mui = require("@ori-ui/mui");
var _checkoutUtils = require("@ori/checkout-utils");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _useClusteredPickupPointsQuery = require("../../api/useClusteredPickupPointsQuery");
var _constants = require("../../constants");
var _SearchConfigurationProvider = require("../../containers/SearchConfigurationProvider");
var _contexts = require("../../contexts");
var _logger = require("../../logger");
var _models = require("../../models");
var _Loading = require("../Loading");
var _shared = require("../shared");
var _jsxRuntime = require("react/jsx-runtime");
const transformLatLng = location => !location ? undefined : {
  latitude: location.lat(),
  longitude: location.lng()
};

// Hiding map container, but keeping map in same initialized state.
// This way, the parent div wrappers should not break the layout.
const MapContainer = (0, _mui.styled)('div')(({
  ownerState: {
    isMapVisible
  }
}) => isMapVisible ? {
  height: '100%',
  width: '100%'
} : _shared.hiddenMapStyles);
const GoogleMap = ({
  activeFilter,
  instantSearchTerm,
  isMapVisible,
  selectedDeliveryChannel
}) => {
  const {
    getTestIdProps
  } = (0, _testidGenerator.default)();
  const {
    credentials,
    minZoom,
    setMapMethods,
    viewSettings,
    setViewSettings
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  const {
    currentLocation,
    setSelectedDelivery
  } = (0, _contexts.useMap)();
  const [isMapLoading, setIsMapLoading] = (0, _react.useState)(false);
  const map = (0, _react.useRef)(null);
  const mapContainer = (0, _react.useRef)(null);

  // Google map doesn't store current markers' references, so storing them here.
  const markers = (0, _react.useRef)([]);
  const getPickupPoints = (0, _useClusteredPickupPointsQuery.useClusteredPickupPointsQuery)(isMapVisible);
  const updatePushpins = (0, _react.useCallback)(async () => {
    // Clean old pins
    markers.current.forEach(item => {
      google.maps.event.clearInstanceListeners(item);
      item.setMap(null);
    });
    markers.current = [];
    const boundsRect = map.current?.getBounds();
    const northEast = boundsRect?.getNorthEast();
    const southWest = boundsRect?.getSouthWest();
    const data = await getPickupPoints({
      filter: activeFilter,
      searchTerm: instantSearchTerm,
      north: northEast?.lat(),
      east: northEast?.lng(),
      south: southWest?.lat(),
      west: southWest?.lng(),
      zoom: viewSettings.zoom
    });
    data.forEach(({
      cluster,
      coordinates: {
        latitude,
        longitude
      },
      displayedClusterSize,
      pickupPoint
    }) => {
      let isSelected = false;
      let icon = '';
      let clickHandler;
      if (pickupPoint) {
        isSelected = pickupPoint.deliveryChannelId === selectedDeliveryChannel;
        icon = (0, _shared.getPickupPointMapIconSvgString)(pickupPoint.deliveryMethod, isSelected, true);
        clickHandler = () => setSelectedDelivery(pickupPoint.deliveryChannelId, {
          latitude,
          longitude
        }, viewSettings.zoom);
      } else {
        const {
          lowerLeft: {
            latitude: south = 0,
            longitude: west = 0
          } = {},
          upperRight: {
            latitude: north = 0,
            longitude: east = 0
          } = {}
        } = cluster ?? {};
        icon = (0, _shared.getClusterMapIconSvgString)(displayedClusterSize, isSelected, true);
        clickHandler = () => {
          // Deselect last selection, if any
          setSelectedDelivery();
          // Zoom to cluster min - max coords
          const usedBounds = new google.maps.LatLngBounds();
          usedBounds.extend(new google.maps.LatLng(north, east));
          usedBounds.extend(new google.maps.LatLng(south, west));
          map.current?.fitBounds(usedBounds, Math.round(_shared.baseMarkerSize / 2));
        };
      }
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(!!pickupPoint, isSelected);
      const item = new google.maps.Marker({
        icon: {
          url: icon,
          anchor: new google.maps.Point(x, y)
        },
        position: {
          lat: latitude,
          lng: longitude
        },
        zIndex: isSelected ? 3 : 2
      });
      item.addListener('click', clickHandler);
      item.setMap(map.current);
      markers.current.push(item);
    });
    if (currentLocation) {
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(false, false);
      const {
        latitude,
        longitude
      } = currentLocation;
      const item = new google.maps.Marker({
        icon: {
          anchor: new google.maps.Point(x, y),
          url: (0, _shared.getCurrentLocationIconSvgString)(true)
        },
        position: {
          lat: latitude,
          lng: longitude
        },
        zIndex: 1
      });
      item.setMap(map.current);
      markers.current.push(item);
    }
  }, [getPickupPoints, activeFilter, instantSearchTerm, viewSettings, currentLocation, selectedDeliveryChannel, setSelectedDelivery]);
  const updateMap = (0, _react.useCallback)(async () => {
    if (!mapContainer.current) {
      return;
    }

    // Initialize map instance
    if (!map.current) {
      const googleMap = new google.maps.Map(mapContainer.current, {
        center: {
          lat: viewSettings.location.latitude,
          lng: viewSettings.location.longitude
        },
        clickableIcons: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        keyboardShortcuts: false,
        mapTypeControl: false,
        minZoom,
        rotateControl: false,
        scaleControl: false,
        zoomControl: false,
        streetViewControl: false,
        zoom: viewSettings.zoom,
        disableDefaultUI: true
      });
      map.current = googleMap;

      // Deselect last selection
      google.maps.event.addListener(googleMap, 'click', () => setSelectedDelivery());

      // Update map view settings
      google.maps.event.addListener(googleMap, 'idle', () => {
        const location = transformLatLng(googleMap.getCenter());
        const zoom = googleMap.getZoom();
        setViewSettings(p => zoom === p.zoom && location?.latitude === p.location.latitude && location.longitude === p.location.longitude ? p : {
          location: location ?? p.location,
          zoom: zoom ?? p.zoom
        });
      });

      // TODO: Remove map methods after flat selection UI is used in all markets
      const getViewSettings = viewOptions => ({
        location: viewOptions?.location ?? transformLatLng(googleMap.getCenter()) ?? viewSettings.location,
        zoom: viewOptions?.zoom ?? googleMap.getZoom() ?? viewSettings.zoom
      });

      // TODO: Remove map methods after flat selection UI is used in all markets
      // Expose the generic map methods
      setMapMethods({
        getViewSettings,
        setLocationOrZoom: viewOptions => {
          const {
            location,
            zoom
          } = getViewSettings(viewOptions);
          googleMap.setOptions({
            center: {
              lat: location.latitude,
              lng: location.longitude
            },
            zoom
          });
          setViewSettings({
            location,
            zoom
          });
        },
        getViewBounds: () => {
          const bounds = googleMap.getBounds();
          const ne = bounds?.getNorthEast();
          const sw = bounds?.getSouthWest();
          return {
            east: ne?.lng() ?? 0,
            north: ne?.lat() ?? 0,
            south: sw?.lat() ?? 0,
            west: sw?.lng() ?? 0
          };
        },
        setViewBounds: ({
          east,
          north,
          south,
          west
        }, padding) => {
          const usedBounds = new google.maps.LatLngBounds();
          usedBounds.extend(new google.maps.LatLng(north, east));
          usedBounds.extend(new google.maps.LatLng(south, west));
          googleMap.fitBounds(usedBounds, padding);
        }
      });
    }

    // Update map view to match view settings
    map.current.setOptions({
      center: {
        lat: viewSettings.location.latitude,
        lng: viewSettings.location.longitude
      },
      zoom: viewSettings.zoom
    });
    await updatePushpins();
    setIsMapLoading(false);
  }, [viewSettings, minZoom, setSelectedDelivery, setMapMethods, updatePushpins, setViewSettings]);

  // Load map script
  (0, _react.useEffect)(() => {
    if (isMapLoading || !isMapVisible) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- wrong 3rd party typings
    if (window.google !== undefined) {
      void updateMap();
    } else {
      // Set callback function to be called when the script is loaded
      window.updateMap = updateMap;
      setIsMapLoading(true);
      (0, _checkoutUtils.loadScript)(`https://maps.googleapis.com/maps/api/js?callback=updateMap&key=${credentials}`, 'google-map-loader-script', _constants.APP_NAME).catch(error => {
        (0, _checkoutUtils.showErrorNotification)();
        (0, _logger.logWarning)('Google map script failed to load. Map will not be displayed.', error);
        setIsMapLoading(false);
      });
    }
  }, [isMapVisible, isMapLoading, updateMap, credentials]);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [isMapLoading && isMapVisible ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Loading.MapLoadingOverlay, {}) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(MapContainer, {
      ref: mapContainer,
      ownerState: {
        isMapVisible
      },
      ...getTestIdProps(_models.TestId.GoogleMapContainer)
    })]
  });
};
exports.GoogleMap = GoogleMap;
var _default = exports.default = GoogleMap;